import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { subDays, format } from 'date-fns'
import { Link } from 'react-router-dom'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import { isEmpty, kebabCase, upperFirst, lowerCase } from 'lodash'
import { v4 as uuid } from 'uuid'
import { IconInfoCircle, IconChartBar, IconSparkles, IconPlayerStop } from '@tabler/icons-react'
import classNames from 'classnames'

import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { Drawer } from '@/components/drawer/Drawer'
import {
  createTrend,
  fetchDynamicInsights,
  fetchTrendCountForOrg,
} from '@/reducers/insights/insightEvents.actions'
import {
  formatCallFilters,
  setSavedFilterById,
  clearSavedFilter,
  clearAllFilters,
} from '@/reducers/callSearch/callSearch.actions'
import { setDateRangeFilters, setFilters } from '@/reducers/callSearch/callSearch.redux'
import { formatPercent } from '@/utils/helpers'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { CALL_EXPLORER_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { resetTrend } from '@/reducers/insights/trends.redux'
import * as dynamicInsightsActions from '@/reducers/insights/dynamicInsightEvents.redux'
import {
  RECENT_QUESTIONS_DYNAMIC_INSIGHTS_FEATURE_NAME,
  CHANNEL_NAME_PLURAL,
} from '@/utils/constants'

import { PoweredByAI } from '@/components/ai/PoweredByAI'
import { Card } from '@/components/cards/Card'
import { CallSearchFilters } from '../Calls/CallSearchFilters'
import { EvidenceContainer } from './components/EvidenceContainer'
import { SuggestedQuestions } from '../../components/interactions/SuggestedQuestions'
import { dynamicInsightsQuestions } from './constants'

import './DynamicInsights.scss'

export const DynamicInsightsPage = () => {
  const dispatch = useDispatch()
  const {
    session_id,
    categories,
    preconditions,
    type,
    stage,
    fraction_irrelevant,
    irrelevant_calls,
    err,
    loading: dynamicInsightLoading,
    precondition_statements,
    precondition_questions,
  } = useSelector((state) => state.dynamicInsightEvents)
  const {
    filters,
    data: { selectedSavedFilterId },
  } = useSelector((state) => state.callSearch)
  const {
    organizationid: currentUserOrg,
    own_organization_id,
    username,
    token,
    hierarchy_manager,
  } = useSelector((state) => state.currentUser)
  const {
    allow_creation: canCreateTrend,
    trend: { id: trendId },
    loading: { trend: trendLoading },
  } = useSelector((state) => state.trends)

  const childOrgSelected = hierarchy_manager && currentUserOrg !== own_organization_id
  const [currentQuestion, setCurrentQuestion] = useState('')
  const [abortController, setAbortController] = useState(null)

  const onSubmit = ({ question }) => {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
      return
    }
    const insightFilters = formatCallFilters(filters)
    insightFilters.question = question
    insightFilters.call_types = [filters.channel]

    const newController = new AbortController()
    const signalDone = () => setAbortController(null)
    setAbortController(newController)
    dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
    dispatch(resetTrend())
    dispatch(
      fetchDynamicInsights(
        insightFilters,
        username,
        token,
        currentUserOrg,
        newController.signal,
        signalDone
      )
    )
    setCurrentQuestion(question)
  }

  const formatSentenceCase = (sentence) => {
    return upperFirst(lowerCase(sentence))
  }

  const handleSelectSavedFilter = (value) => {
    if (!value) {
      dispatch(clearSavedFilter(false))
    } else {
      dispatch(setSavedFilterById(value, false))
    }
  }

  useEffect(() => {
    // default to 60 days
    const now = new Date()
    const sixtyDaysAgo = format(subDays(now, 59), "yyyy-MM-dd'T00:00:00'xxx")
    const today = format(now, "yyyy-MM-dd'T23:59:59'xxx")

    // default to include calls w/o audio
    const audioFilter = { includeCallsWithoutAudio: true }

    dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
    dispatch(clearAllFilters())
    dispatch(setDateRangeFilters({ startDate: sixtyDaysAgo, endDate: today }))
    dispatch(setFilters(audioFilter))
    dispatch(fetchSavedFilters(CALL_EXPLORER_FILTER_TYPE))
  }, [])

  const orgId = filters.organizationId || currentUserOrg

  useEffect(() => {
    // Fetch trend count for the selected organization (or current user's org if none selected)
    dispatch(fetchTrendCountForOrg(orgId))
  }, [orgId])

  const trackInsight = async () => {
    const subCategories = categories.map((category) => {
      return {
        name: category.name,
        description: category.description,
      }
    })
    const selectedOrgId = filters.organizationId || currentUserOrg
    const taxonomyBody = {
      name: currentQuestion,
      description: currentQuestion,
      sub_categories: subCategories,
      requested_organization_id: selectedOrgId,
    }

    const insightBody = {
      call_filters: formatCallFilters(filters),
      question: currentQuestion,
      name: currentQuestion,
      taxonomy_uuid: null,
      requested_organization_id: selectedOrgId,
      backfill: true, // set to true when ready for real data
      precondition_statements,
      precondition_questions,
    }

    await dispatch(createTrend(taxonomyBody, insightBody))
    dispatch(fetchTrendCountForOrg(selectedOrgId))
  }

  const validationSchema = Yup.object().shape({
    question: Yup.string().required('You must enter a question'),
  })

  return (
    <div className="insights-container">
      <PageHeader
        data-testid="insights-page"
        decoration="lightning-bolt"
        title="Discover"
        actions={
          !childOrgSelected ? (
            <SavedFilters
              filterType={CALL_EXPLORER_FILTER_TYPE}
              selectedSavedFilterId={selectedSavedFilterId}
              handleSelectSavedFilter={handleSelectSavedFilter}
              filterFormOptions={[
                'name',
                'isDefault',
                'filterPropertiesHeader',
                'dateRange',
                'agents',
                'tags',
                'playbooks',
                'callDuration',
                'maxCallDuration',
                'includeCallsWithoutAudio',
                'isWin',
                'keywordHeader',
                'keywords',
                'keywordOptions.side',
                'keywordOptions.logic',
                'playbookPropertiesHeader',
                'checklist',
                'deck',
                'notifications',
                'postcall',
                'scorecards',
                'scorecardThreshold',
                'scoredStatus',
              ]}
            />
          ) : null
        }
      />
      <CallSearchFilters childOrgSelected={childOrgSelected} allow120Days />
      <div className="insights-wrapper">
        <Formik
          enableReinitialize
          onSubmit={onSubmit}
          initialValues={{ question: '' }}
          validationSchema={validationSchema}
          validateOnMount
        >
          {({ errors, isValid, handleSubmit, setFieldValue }) => {
            const submitDisabled = !isValid || !isEmpty(errors)
            const channelName = CHANNEL_NAME_PLURAL[filters.channel]?.toLowerCase()
            const placeholderText = `Curious about patterns in your ${channelName}? Ask anything or try a suggested question`

            return (
              <>
                <form onSubmit={handleSubmit} className="mb mt">
                  <h3>What would you like to know about your customer conversations?</h3>
                  <div
                    className={classNames({ 'animated-gradient-border': dynamicInsightLoading })}
                  >
                    <div className="fake-textarea">
                      <Field name="question" placeholder={placeholderText} as="textarea" rows={3} />
                      <div className="flex-end">
                        {!abortController && (
                          <Button
                            data-testid="dynamic-insights-submit-button"
                            brand
                            type="submit"
                            disabled={submitDisabled}
                            disabledTooltip={Object.values(errors)
                              .filter((error) => error)
                              .join(' ')}
                          >
                            <IconSparkles />
                            Find Answer
                          </Button>
                        )}
                        {abortController && (
                          <Button
                            destructive
                            data-testid="dynamic-insights-submit-button"
                            type="submit"
                          >
                            <IconPlayerStop />
                            Stop
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
                {type === 'progress' && (
                  <div className="results-container mb">
                    <div className="text-center">
                      <small>
                        {!stage ? (
                          'Balto AI is finding the answer to your question sampling hundreds of calls.'
                        ) : (
                          <span data-testid="insights-stage">{stage}</span>
                        )}
                      </small>
                    </div>
                  </div>
                )}
                <SuggestedQuestions
                  initialCategory="sales"
                  feature={RECENT_QUESTIONS_DYNAMIC_INSIGHTS_FEATURE_NAME}
                  questions={dynamicInsightsQuestions}
                  onQuestionClick={(value) => setFieldValue('question', value)}
                  autoCollapse={type === 'progress' || type === 'result'}
                />
              </>
            )
          }}
        </Formik>

        {type === 'result' && err && (
          <Card className="results-container mt">
            <ErrorMessage content={err} data-testid="insights-error" style={{ marginTop: 0 }} />
          </Card>
        )}

        {type === 'result' && !err && (
          <Card className="results-container mt">
            <div className="insights-grid" data-testid="insights-results">
              <div className="flex-align-start flex-space-between small-gap title">
                <div>
                  <h2 data-testid="title">{formatSentenceCase(currentQuestion)}</h2>
                  <div className="muted-text">
                    <PoweredByAI />
                  </div>
                </div>
                {!isEmpty(categories) && (
                  <div className="flex-space-between gap">
                    <div className="flex-align-center">
                      <InteractiveTooltip content="Trends let you monitor key insights over time across your organization, so you can spot changes as they happen.">
                        <div className="clickable flex-align-center small-gap status-muted no-wrap">
                          <IconInfoCircle className="icon-svg" /> What are Trends?
                        </div>
                      </InteractiveTooltip>
                    </div>
                    {!trendLoading && trendId == null && (
                      <Button
                        brand
                        data-testid="save-trend-button"
                        disabled={!canCreateTrend}
                        disabledTooltip="You have hit the limit of trends available on your plan. Reach out to your Customer Success Manager if you would like to add more."
                        onClick={trackInsight}
                      >
                        <IconChartBar />
                        Save Trend
                      </Button>
                    )}
                    {trendLoading && (
                      <Button brand loading disabled disabledTooltip="Trend is loading...">
                        Saving...
                      </Button>
                    )}
                    {trendId != null && (
                      <Link
                        to="/trends"
                        className="balto-button default no-wrap"
                        data-testid="view-trend-link"
                      >
                        View Trend
                      </Link>
                    )}
                  </div>
                )}
              </div>
              {isEmpty(categories) && (
                <div data-testid="insights-no-answers">No answers were found.</div>
              )}
              {!isEmpty(categories) && (
                <>
                  <div className="muted-text">Topics</div>
                  <div className="muted-text">Est. Frequency</div>
                  <div>&nbsp;</div>
                  {categories.map((category, index) => {
                    const { calls = [] } = category
                    const callsWithExcerpts = calls.filter((call) => !isEmpty(call.excerpts))
                    return (
                      <React.Fragment key={kebabCase(category.name)}>
                        <div className="flex-align-self-end bold">{category.name}</div>
                        <div className="flex-align-self-end bold">
                          {formatPercent(category.fraction * 100, 0, true)}
                        </div>
                        <div className="flex-end">
                          {!isEmpty(callsWithExcerpts) && (
                            <Drawer
                              id={`evidence-${kebabCase(category.name)}`}
                              wide
                              title="Evidence in conversations"
                              trigger={(handleOpen) => (
                                <Button
                                  type="button"
                                  onClick={handleOpen}
                                  data-testid={`evidence-button-${index}`}
                                >
                                  See Evidence
                                </Button>
                              )}
                            >
                              <EvidenceContainer
                                calls={callsWithExcerpts}
                                session_id={session_id}
                              />
                            </Drawer>
                          )}
                        </div>
                        <div className="description">{category.description}</div>
                      </React.Fragment>
                    )
                  })}
                </>
              )}
              <div className="footer">
                <div className="flex-align-center flex-space-between mt">
                  <span>
                    {formatPercent(fraction_irrelevant * 100, 0, true)} of conversations were not
                    applicable to the question
                  </span>
                  <div>
                    {!isEmpty(irrelevant_calls) && (
                      <Drawer
                        id="evidence-irrelevant"
                        wide
                        title="Examples of non-applicable conversations"
                        trigger={(handleOpen) => (
                          <Button
                            type="button"
                            onClick={handleOpen}
                            data-testid="evidence-button-irrelevant"
                          >
                            Show Examples
                          </Button>
                        )}
                      >
                        <div>
                          <div className="evidence-container">
                            {!isEmpty(preconditions) && (
                              <Card>
                                <div data-testid="insights-preconditions">
                                  <h3>Relevancy Conditions</h3>
                                  <p>
                                    Conversations not meeting these implied assumptions are excluded
                                    as not applicable:
                                  </p>
                                  <ul>
                                    {preconditions.map((precondition) => {
                                      return (
                                        <li key={uuid()}>{formatSentenceCase(precondition)}</li>
                                      )
                                    })}
                                  </ul>
                                </div>
                              </Card>
                            )}
                          </div>

                          <EvidenceContainer calls={irrelevant_calls} session_id={session_id} />
                        </div>
                      </Drawer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default DynamicInsightsPage
